// @flow
import * as React from 'react';
import styled from 'styled-components';
import { listingSearchHomePath } from 'utils/searchHomePages';
import Box from 'components/Box';
import Flex from 'components/Flex';
import SearchTile from 'components/SearchTile';
import Img, { LogoImage } from 'components/Image';
import { H4, BodySmall } from 'components/Text';
import Link from 'components/Link';
import { Link as RouterLink } from 'components/RouterLink';
import type { RandomGradProgram } from 'api/shared';
import { trackClicked } from 'utils/analytics';
import { shuffleArray } from 'utils/functional';
import { scrollToTop } from 'utils/scroll';
import { breakpoints, colors } from '../../theme';

const { useState, useEffect } = React;

const TileLink = styled(props => <Box component={RouterLink} {...props} />)`
  display: block;
  color: inherit;
  text-decoration: none;
`;

const IdealistLogo = styled(Box)`
  img {
    width: 100%;
  }
`;

const LogoContainer = styled(Flex)`
  justify-content: center;

  /* only one grad program shows up */

  > div {
    display: none;
  }
  > div:first-of-type {
    display: block;
    margin: 0 auto;
  }
  @media (min-width: ${breakpoints[0]}) {
    /* three grad programs show up */

    > div {
      display: block;
      max-width: 160px;
    }
    > div:first-of-type {
      margin: 0 0 0 20px;
    }
    > div:nth-last-child(2),
    > div:last-of-type {
      display: none;
    }
  }
  @media (min-width: ${breakpoints[1]}) {
    /* all five grad programs show up */

    > div:nth-last-child(2),
    > div:last-of-type {
      display: block;
    }
  }
  @media (min-width: ${breakpoints[2]}) {
    /* all but the last grad program show up */

    > div:last-of-type {
      display: none;
    }
  }
  @media (min-width: ${breakpoints[3]}) {
    /* all five grad programs show up */

    > div:last-of-type {
      display: block;
    }
  }
`;

const GradSchoolsAd = ({
  programs,
  setSearchFilter
}: {
  programs: Array<RandomGradProgram>,
  setSearchFilter: Function
}) => {
  const [shuffledPrograms, setShuffledPrograms] = useState(programs || []);

  useEffect(
    () => {
      if (!programs) return;
      const programsWithLogo = programs.filter(program => program.logo);
      setShuffledPrograms(
        shuffleArray(
          programsWithLogo && programsWithLogo.length >= 5
            ? programsWithLogo
            : programs
        ).slice(0, 5)
      );
    },
    [programs]
  );

  return (
    <SearchTile pt={[24, null, 36]} pb={[24, null, 48]}>
      <TileLink
        data-qa-id="grad-schools-ad"
        title="View graduate schools"
        to={`${listingSearchHomePath('GRAD_PROGRAM')}?searchMode=true&q=`}
        onClick={() => {
          scrollToTop();
          trackClicked('Grad Schools Ad');
          setSearchFilter('type', 'GRAD_PROGRAM');
        }}
      >
        <Box
          display={['block', 'flex']}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box display={['block', 'flex']} alignItems="flex-start">
            <IdealistLogo width={[64, 120]} height={[64, 120]}>
              <Img
                flex="0 0 auto"
                src="/static/images/logo-mark.png"
                srcSet="/static/images/logo-mark@2x.png, /static/images/logo-mark@3x.png 2x"
                alt="Idealist logo"
              />
            </IdealistLogo>
            <Box pl={[0, 30]} mt={[24, 0]}>
              <H4 mb="4px" lineHeight="1.375">
                {getText('Thinking about graduate school?')}
              </H4>
              <BodySmall>
                {getText(
                  'Discover a variety of grad programs in our new directory'
                )}
              </BodySmall>
            </Box>
          </Box>
          <Box pl={[0, 12]} flex="0 0 auto">
            <Link as="div">
              <BodySmall styleAs="h5">{getText('Learn More')}</BodySmall>
            </Link>
          </Box>
        </Box>
        <Box mt={[24, null, 40]} ml={-20} width="100% + 20px">
          <LogoContainer>
            {shuffledPrograms.map(program => (
              <Box key={program.id} data-qa-id="grad-ad" ml="20px" width="100%">
                <Box
                  position="relative"
                  maxWidth={[160, 'none']}
                  width={['calc(100% - 92px)', '100%']}
                  m="0 auto"
                  borderRadius={12}
                  bg={colors.offWhite}
                >
                  <Box pb="100%" height={0}>
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      width="calc(100% - 24px)"
                      height="calc(100% - 24px)"
                      pointerEvents="none"
                    >
                      <LogoImage
                        width="100%"
                        height="100%"
                        fsWidth={160}
                        fsHeight={160}
                        handle={program.logo ? program.logo.handle : undefined}
                        alt={program.name}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </LogoContainer>
        </Box>
      </TileLink>
    </SearchTile>
  );
};

export default GradSchoolsAd;
